import React, { useEffect, useState } from "react";
import "./style.css";
import { Tag, message } from "antd";
import { getServ } from "../../../API/serv&rem";
import { useDateFilter } from "../../../Contexts/DateFilterContext";
import BarChart from "../../../Components/Charts/BarChart";
import GenericTable from "../../../Components/GenericTable";
import Lottie from "lottie-react";
import reportGeneration from "../../../Assets/Animations/Report Generation.json";

const ServRem = () => {
  const defaultServData = {
    scheduled: 0,
    scheduled_cost: 0,
    due: 0,
    due_cost: 0,
    completed: 0,
    completed_cost: 0,
    expired: 0,
    expired_cost: 0,
    total_expenses: 0,
    cost_trend: {},
    upcoming_schedule: [],
  };
  const [servData, setServData] = useState(defaultServData);
  const [loading, setLoading] = useState(true);
  const { from, to } = useDateFilter();

  useEffect(() => {
    if (!to || !from) {
      return;
    }

    const fetchServ = async () => {
      try {
        setLoading(true);
        const response = await getServ(to, from);
        setServData(response.data);
        setLoading(false);
      } catch (error) {
        message.error("Failed to fetch fuel data");
        console.log(error, "Failed to fetch fuel data");
      }
      setLoading(false);
    };

    fetchServ();
  }, [to, from]);

  const upcomingCols = [
    {
      title: <span className="vehicle-title">Vehicle Name</span>,
      dataIndex: "vehicle_name",
      key: "vehicle_name",
      sorter: (a, b) =>
        a.vehicle_name.localeCompare(b.vehicle_name, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      sorter: (a, b) =>
        a.description.localeCompare(b.description, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Schedule Type",
      dataIndex: "schedule_type",
      key: "schedule_type",
      sorter: (a, b) => a.schedule_type.localeCompare(b.schedule_type),
      render: (text) => {
        let color;
        if (text === "repairing") {
          color = "#01CFC3";
        } else if (text === "servicing") {
          color = "#FFD700";
        } else if (text === "inspection") {
          color = "#1E90FF";
        } else if (text === "renewal") {
          color = "#32CD32";
        } else {
          color = "#FF004D";
        }
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Tag",
      dataIndex: "tag",
      key: "tag",
      sorter: (a, b) => a.tag.localeCompare(b.tag),

      render: (text) => {
        let color;
        if (text === "due") {
          color = "#DC143C"; // crimson for due
        } else if (text === "upcoming") {
          color = "#FF8C00"; // dark orange for upcoming
        } else if (text === "scheduled") {
          color = "#4682B4"; // steel blue for scheduled
        } else if (text === "due soon") {
          color = "#8B4513"; // saddle brown for due soon
        } else {
          color = "#708090"; // slate gray as a default color
        }
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        a.date.localeCompare(b.date, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Est. Cost",
      dataIndex: "est_cost",
      key: "est_cost",
      sorter: (a, b) =>
        a.est_cost.localeCompare(b.est_cost, undefined, {
          numeric: true,
        }),
      render: (text) => {
        return <>$ {text}</>;
      },
    },
  ];

  return (
    <div className="serv-rem">
      {loading ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          className="loader-container"
        >
          <Lottie
            animationData={reportGeneration}
            loop={true}
            style={{ width: 150, height: 150 }}
          />
        </div>
      ) : (
        <div className="rows-container">
          <div className="row tabs">
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Total Expense($)</span>
                <span className="value-box" style={{ color: "#B73AFF" }}>
                  ${servData?.total_expenses}
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Completed</span>
                <span className="value-box" style={{ color: "#1FC16B" }}>
                  {servData?.completed}
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Completed Cost($)</span>
                <span className="value-box" style={{ color: "#1FC16B" }}>
                  ${servData?.completed_cost}
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Scheduled</span>
                <span className="value-box" style={{ color: "#155DFC" }}>
                  {servData?.scheduled}
                </span>
              </div>
            </div>
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Scheduled Cost($)</span>
                <span className="value-box" style={{ color: "#155DFC" }}>
                  ${servData?.scheduled_cost}
                </span>
              </div>
            </div>
          </div>
          <div className="row tabs">
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Due</span>
                <span className="value-box" style={{ color: "#DFB400" }}>
                  {servData?.due}
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Due Cost($)</span>
                <span className="value-box" style={{ color: "#DFB400" }}>
                  ${servData?.due_cost}
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Expired </span>
                <span className="value-box" style={{ color: "#FB3748" }}>
                  {servData?.expired}
                </span>
              </div>
            </div>
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Expired Cost($)</span>
                <span className="value-box" style={{ color: "#FB3748" }}>
                  ${servData?.expired_cost}
                </span>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="graph">
              <BarChart
                graphData={servData?.cost_trend || {}}
                lengthKeys={1}
                colourTheme={["#2662D9"]}
                graphTitle="Cost Trend"
                selectTenByDefault={true}
                units={["$"]}
                classNameCSS="cost-trend"
              />
            </div>
          </div>
          <GenericTable
            title="Upcoming Schedule"
            dataSource={servData?.upcoming_schedule}
            columns={upcomingCols}
          />
        </div>
      )}
    </div>
  );
};

export default ServRem;
